//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

/**
 * The books check: every rule the money records must satisfy together, as
 * one pure function. The tests run it after every simulated action, and the
 * Superadmin's settings page runs it on the live data, so "the figures agree"
 * is something the system can show rather than something to take on trust.
 *
 *  - the stored pay rows (Payout) are exactly what the payroll engine computes;
 *  - an approved receipt is exactly one ledger expense that follows it, and
 *    no other receipt has one (payroll rule 3); the entry is in a category of
 *    the component the receipt was approved as (salary, the quarter bonus,
 *    premiya, or holiday money, which is never pay);
 *  - every category is well-formed (a component that exists, holiday money
 *    as an expense);
 *  - every payroll entry of one employee-month carries the month's one rate
 *    (rule 5);
 *  - each quarter score carries the engine's figures and there is one per
 *    employee and quarter;
 *  - one salary record per employee, its history ending in its current values;
 *  - every ledger entry, salary record, score and bonus is well-formed
 *    (ledger.ts).
 *
 * Problems that are not contradictions but probably mistakes (a salary
 * category entry that names nobody) come back as warnings.
 */

import { type Employee } from '@hcengineering/contact'
import { type Doc, type Ref } from '@hcengineering/core'
import { categoryProblem, compensationProblem, ledgerEntryProblem, quarterScoreProblem, spotBonusProblem } from './ledger'
import {
  categoryComponents,
  companyPayroll,
  isPaidMonth,
  isPayrollTransaction,
  monthIdx,
  payMonthOf,
  payrollCategorySet,
  payrollSpan,
  quarterScoreFigures,
  rateSetter,
  receiptComponent,
  type PayrollInput
} from './payroll'
import {
  type Compensation,
  type ComponentFigures,
  type FinanceAccount,
  type PaidComponent,
  type Payout,
  type QuarterScore,
  type Receipt,
  type SpotBonus,
  type Transaction,
  type TransactionCategory
} from './types'

/** Everything the check reads. `transactions` is the whole ledger. */
export interface Books {
  compensations: Compensation[]
  scores: QuarterScore[]
  spots: SpotBonus[]
  transactions: Transaction[]
  categories: TransactionCategory[]
  accounts: FinanceAccount[]
  receipts: Receipt[]
  payouts: Payout[]
  /** Today's rate (DefaultFxRate). */
  currentRate: number
  now?: number
}

export type BooksProblemCode =
  | 'payout-missing'
  | 'payout-differs'
  | 'payout-extra'
  | 'payout-duplicate'
  | 'receipt-entry-count'
  | 'receipt-entry-differs'
  | 'receipt-entry-orphan'
  | 'month-rate'
  | 'score-duplicate'
  | 'score-stamp'
  | 'score-invalid'
  | 'salary-duplicate'
  | 'salary-history'
  | 'salary-invalid'
  | 'spot-invalid'
  | 'spot-unpaid'
  | 'entry-invalid'
  | 'entry-nobody'
  | 'category-invalid'
  | 'no-salary-record'

export interface BooksProblem {
  code: BooksProblemCode
  severity: 'error' | 'warning'
  /** The record at fault, when there is one. */
  ref?: Ref<Doc>
  employee?: Ref<Employee>
  year?: number
  month?: number
  /** For logs and tests; pages translate `code`. */
  detail: string
}

const PAYOUT_FIELDS = [
  'expectedUsd',
  'paidUsd',
  'expectedUzs',
  'paidUzs',
  'returnedUzs',
  'rate',
  'salaryUzs',
  'quarterBonusUzs',
  'premiyaUzs',
  'paidSalaryUzs',
  'paidQuarterBonusUzs',
  'paidPremiyaUzs',
  'holidayUzs'
] as const

/** The pay row the engine wants for a month (what OnPayrollChange stores). */
export function payoutFigures (m: {
  expectedUsd: number
  netPaidUsd: number
  expectedUzs: number
  paidUzs: number
  returnedUzs: number
  rate: number
  components: Record<PaidComponent, ComponentFigures>
  holidayUzs: number
}): Record<(typeof PAYOUT_FIELDS)[number], number> {
  return {
    expectedUsd: m.expectedUsd,
    paidUsd: m.netPaidUsd,
    expectedUzs: m.expectedUzs,
    paidUzs: m.paidUzs,
    returnedUzs: m.returnedUzs,
    rate: m.rate,
    salaryUzs: m.components.salary.expectedUzs,
    quarterBonusUzs: m.components.quarterBonus.expectedUzs,
    premiyaUzs: m.components.premiya.expectedUzs,
    paidSalaryUzs: m.components.salary.netPaidUzs,
    paidQuarterBonusUzs: m.components.quarterBonus.netPaidUzs,
    paidPremiyaUzs: m.components.premiya.netPaidUzs,
    holidayUzs: m.holidayUzs
  }
}

export function checkBooks (books: Books): BooksProblem[] {
  const problems: BooksProblem[] = []
  const error = (code: BooksProblemCode, detail: string, at: Partial<BooksProblem> = {}): void => {
    problems.push({ code, severity: 'error', detail, ...at })
  }
  const warning = (code: BooksProblemCode, detail: string, at: Partial<BooksProblem> = {}): void => {
    problems.push({ code, severity: 'warning', detail, ...at })
  }

  const input: PayrollInput = {
    compensations: books.compensations,
    scores: books.scores,
    spots: books.spots,
    transactions: books.transactions,
    categories: books.categories,
    receipts: books.receipts,
    currentRate: books.currentRate,
    now: books.now
  }
  const payrollCats = payrollCategorySet(books.categories)
  const components = categoryComponents(books.categories)
  const categoryById = new Map(books.categories.map((c) => [c._id, c]))
  const accountIds = new Set(books.accounts.map((a) => a._id))

  // ---- categories ---------------------------------------------------------------
  for (const c of books.categories) {
    const why = categoryProblem(c)
    if (why !== undefined) error('category-invalid', `category ${c._id}: ${why}`, { ref: c._id })
  }

  // ---- pay rows = the engine ---------------------------------------------------
  const span = payrollSpan(input)
  const payroll = companyPayroll(input, span.from, span.to)
  const rows = new Map<string, Payout[]>()
  for (const p of books.payouts) {
    const k = `${p.employee}:${p.year}-${p.month}`
    rows.set(k, [...(rows.get(k) ?? []), p])
  }
  const wanted = new Set<string>()
  for (const [employee, months] of payroll) {
    for (const m of months) {
      const k = `${employee}:${m.year}-${m.month}`
      wanted.add(k)
      const at = { employee, year: m.year, month: m.month }
      const stored = rows.get(k) ?? []
      if (stored.length === 0) {
        error('payout-missing', `no pay row for ${k}`, at)
        continue
      }
      if (stored.length > 1) error('payout-duplicate', `${stored.length} pay rows for ${k}`, { ...at, ref: stored[1]._id })
      const want = payoutFigures(m)
      for (const f of PAYOUT_FIELDS) {
        if (stored[0][f] !== want[f]) {
          error('payout-differs', `${k} ${f}: stored ${String(stored[0][f])}, engine ${want[f]}`, { ...at, ref: stored[0]._id })
        }
      }
    }
  }
  for (const [k, stored] of rows) {
    if (!wanted.has(k)) {
      error('payout-extra', `pay row ${k} matches no month of the engine`, {
        ref: stored[0]._id,
        employee: stored[0].employee,
        year: stored[0].year,
        month: stored[0].month
      })
    }
  }

  // ---- receipts and their ledger entries ---------------------------------------
  const entriesByReceipt = new Map<string, Transaction[]>()
  for (const t of books.transactions) {
    if (t.receipt == null) continue
    entriesByReceipt.set(t.receipt, [...(entriesByReceipt.get(t.receipt) ?? []), t])
  }
  const receiptIds = new Set<string>(books.receipts.map((r) => r._id))
  for (const r of books.receipts) {
    const entries = entriesByReceipt.get(r._id) ?? []
    const at = { ref: r._id, employee: r.employee, year: r.year, month: r.month }
    const want = r.status === 'approved' ? 1 : 0
    if (entries.length !== want) {
      error('receipt-entry-count', `${r.status} receipt ${r._id} has ${entries.length} ledger entries`, at)
      continue
    }
    if (want === 0) continue
    const e = entries[0]
    // The entry settles what the receipt was approved as (rule 3).
    const follows =
      e.kind === 'expense' &&
      e.amountUzs === r.amountUzs &&
      e.employee === r.employee &&
      e.payYear === r.year &&
      e.payMonth === r.month &&
      e.category != null &&
      components.get(e.category) === receiptComponent(r)
    if (!follows) error('receipt-entry-differs', `the ledger entry of receipt ${r._id} does not follow it`, { ...at, ref: e._id })
  }
  for (const [receipt, entries] of entriesByReceipt) {
    if (!receiptIds.has(receipt)) {
      error('receipt-entry-orphan', `ledger entry ${entries[0]._id} points at a receipt that is gone`, { ref: entries[0]._id })
    }
  }

  // ---- one rate per employee-month ----------------------------------------------
  const byMonth = new Map<string, Transaction[]>()
  for (const t of books.transactions) {
    if (!isPayrollTransaction(t, payrollCats)) continue
    const { year, month } = payMonthOf(t)
    const k = `${t.employee as string}:${monthIdx(year, month)}`
    byMonth.set(k, [...(byMonth.get(k) ?? []), t])
  }
  for (const entries of byMonth.values()) {
    const rate = rateSetter(entries)?.fxRate
    for (const t of entries) {
      if (t.fxRate !== rate) {
        const { year, month } = payMonthOf(t)
        error('month-rate', `entry ${t._id} carries rate ${String(t.fxRate)}, its month ${String(rate)}`, {
          ref: t._id,
          employee: t.employee as Ref<Employee>,
          year,
          month
        })
      }
    }
  }

  // ---- salary records -------------------------------------------------------------
  const compsByEmployee = new Map<Ref<Employee>, Compensation[]>()
  for (const c of books.compensations) compsByEmployee.set(c.employee, [...(compsByEmployee.get(c.employee) ?? []), c])
  const salaryRecord = (employee: Ref<Employee>): Compensation | undefined =>
    [...(compsByEmployee.get(employee) ?? [])].sort((a, b) => b.effectiveFrom - a.effectiveFrom)[0]
  for (const [employee, list] of compsByEmployee) {
    if (list.length > 1) error('salary-duplicate', `${list.length} salary records for ${employee}`, { employee, ref: list[1]._id })
  }
  for (const c of books.compensations) {
    const why = compensationProblem(c)
    if (why !== undefined) {
      error('salary-invalid', `salary record ${c._id}: ${why}`, { ref: c._id, employee: c.employee })
      continue
    }
    const history = [...(c.salaryHistory ?? [])].sort((a, b) => monthIdx(a.year, a.month) - monthIdx(b.year, b.month))
    const last = history[history.length - 1]
    if (last !== undefined && (last.baseSalaryUsd !== c.baseSalaryUsd || last.bonusPct !== c.bonusPct)) {
      error('salary-history', `the history of salary record ${c._id} does not end in its current values`, {
        ref: c._id,
        employee: c.employee
      })
    }
  }

  // ---- quarter scores -------------------------------------------------------------
  const scoreKeys = new Set<string>()
  for (const s of books.scores) {
    const at = { ref: s._id, employee: s.employee, year: s.year }
    const why = quarterScoreProblem(s)
    if (why !== undefined) {
      error('score-invalid', `score ${s._id}: ${why}`, at)
      continue
    }
    const k = `${s.employee}:${s.year}-Q${s.quarter}`
    if (scoreKeys.has(k)) error('score-duplicate', `two scores for ${k}`, at)
    scoreKeys.add(k)
    const comp = salaryRecord(s.employee)
    if (comp === undefined) {
      warning('no-salary-record', `score ${s._id} belongs to someone with no salary record`, at)
      continue
    }
    const want = quarterScoreFigures(comp, s.year, s.quarter, s.kpi)
    if (
      s.kpi !== want.kpi ||
      s.baseUsd !== want.baseUsd ||
      s.bonusPct !== want.bonusPct ||
      s.quarterlyBonusUsd !== want.quarterlyBonusUsd
    ) {
      error('score-stamp', `score ${k} does not carry the engine's figures`, at)
    }
  }

  // ---- spot bonuses -----------------------------------------------------------------
  for (const s of books.spots) {
    const at = { ref: s._id, employee: s.employee, year: s.year, month: s.month }
    const why = spotBonusProblem(s)
    if (why !== undefined) {
      error('spot-invalid', `spot bonus ${s._id}: ${why}`, at)
      continue
    }
    const comp = salaryRecord(s.employee)
    if (comp === undefined) {
      warning('no-salary-record', `spot bonus ${s._id} belongs to someone with no salary record`, at)
    } else if (s.amountUzs > 0 && !isPaidMonth(comp, monthIdx(s.year, s.month))) {
      // Outside the pay start .. last paid month the engine expects nothing.
      warning('spot-unpaid', `spot bonus ${s._id} is for a month no salary is expected for`, at)
    }
  }

  // ---- ledger entries -----------------------------------------------------------------
  for (const t of books.transactions) {
    const category = t.category != null ? categoryById.get(t.category) : undefined
    const why = ledgerEntryProblem(t, { category, accountExists: accountIds.has(t.account) })
    if (why !== undefined) {
      error('entry-invalid', `ledger entry ${t._id}: ${why}`, { ref: t._id })
      continue
    }
    // An entry of a pay component that names nobody is counted toward nobody's pay.
    if (category?.component != null && t.employee == null) {
      warning('entry-nobody', `ledger entry ${t._id} is in a pay category but names no employee`, { ref: t._id })
    }
    if (isPayrollTransaction(t, payrollCats) && salaryRecord(t.employee as Ref<Employee>) === undefined) {
      const { year, month } = payMonthOf(t)
      warning('no-salary-record', `ledger entry ${t._id} pays someone with no salary record`, {
        ref: t._id,
        employee: t.employee as Ref<Employee>,
        year,
        month
      })
    }
  }

  return problems
}
